import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Divider,
  List,
  Chip,
  Grid,
  Paper,
  useTheme
} from '@mui/material';
import { 
  DateRange, 
  Warning, 
  TimerOutlined, 
  ScheduleOutlined,
  CalendarMonthOutlined
} from '@mui/icons-material';
import moment from 'moment';
import NoData from 'components/Common/NoData';
import MonitoringList from './MonitoringList';

interface MonitoringDatesSummaryProps {
  overDue?: any[];
  dueInAMonth?: any[];
  upcomingHighRiskMaps?: any[];
}

const MonitoringDatesSummary: React.FC<MonitoringDatesSummaryProps> = ({
  overDue = [],
  dueInAMonth = [],
  upcomingHighRiskMaps = []
}) => {
  const theme = useTheme();
  
  // Calculate summary metrics
  const totalPendingMaps = (overDue?.length || 0) + (dueInAMonth?.length || 0) + (upcomingHighRiskMaps?.length || 0);
  const earliestDueDate = overDue?.length > 0 ? moment.min(overDue.map((map) => moment(map.next_monitoring_date))) : null;
  const urgencyLevel = overDue?.length > 3 ? 'Critical' : overDue?.length > 0 ? 'High' : dueInAMonth?.length > 5 ? 'Medium' : 'Low';
  
  return (
    <Box>
      {/* Header with summary metrics */}
      <Box mb={1}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 0.5
        }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 'bold', 
              display: 'flex', 
              alignItems: 'center',
              gap: 0.5
            }}
          >
            <DateRange fontSize="small" color="primary" /> 
            Next Monitoring Dates
          </Typography>
          <Box>
            <Chip 
              icon={<TimerOutlined fontSize="small" />}
              label={`Urgency: ${urgencyLevel}`}
              color={
                urgencyLevel === 'Critical' ? 'error' :
                urgencyLevel === 'High' ? 'warning' :
                urgencyLevel === 'Medium' ? 'info' : 'success'
              }
              size="small"
              sx={{ fontWeight: 'bold', height: '20px', '& .MuiChip-label': { fontSize: '0.7rem' } }}
            />
          </Box>
        </Box>
        
        {/* Summary metrics as chips */}
        <Grid container spacing={0.5} mb={0.5}>
          <Grid item xs={6} sm={3}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 0.5, 
                bgcolor: theme.palette.error.light,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '4px'
              }}
            >
              <Typography variant="caption" color="error.dark" fontWeight="bold">Overdue</Typography>
              <Chip 
                label={overDue?.length || 0} 
                size="small" 
                color="error"
                sx={{ fontWeight: 'bold', height: '16px', '& .MuiChip-label': { fontSize: '0.6rem', px: 0.5 } }}
              />
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 0.5, 
                bgcolor: theme.palette.warning.light,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '4px'
              }}
            >
              <Typography variant="caption" color="warning.dark" fontWeight="bold">Due in Month</Typography>
              <Chip 
                label={dueInAMonth?.length || 0} 
                size="small" 
                color="warning"
                sx={{ fontWeight: 'bold', height: '16px', '& .MuiChip-label': { fontSize: '0.6rem', px: 0.5 } }}
              />
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 0.5, 
                bgcolor: theme.palette.info.light,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '4px'
              }}
            >
              <Typography variant="caption" color="info.dark" fontWeight="bold">Upcoming</Typography>
              <Chip 
                label={upcomingHighRiskMaps?.length || 0} 
                size="small" 
                color="info"
                sx={{ fontWeight: 'bold', height: '16px', '& .MuiChip-label': { fontSize: '0.6rem', px: 0.5 } }}
              />
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 0.5, 
                bgcolor: theme.palette.primary.light,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '4px'
              }}
            >
              <Typography variant="caption" color="primary.dark" fontWeight="bold">Total</Typography>
              <Chip 
                label={totalPendingMaps} 
                size="small" 
                color="primary"
                sx={{ fontWeight: 'bold', height: '16px', '& .MuiChip-label': { fontSize: '0.6rem', px: 0.5 } }}
              />
            </Paper>
          </Grid>
        </Grid>
        
        {earliestDueDate && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              bgcolor: overDue?.length > 0 ? theme.palette.error.light : theme.palette.warning.light,
              p: 0.5,
              borderRadius: '4px',
              mb: 0.5
            }}
          >
            <Warning color="error" fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption" fontWeight="bold">
              Earliest Due: {earliestDueDate.format('DD MMM YYYY')} ({earliestDueDate.fromNow()})
            </Typography>
          </Box>
        )}
      </Box>
      
      <Divider />

      {/* Overdue Maps */}
      {overDue && overDue.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarMonthOutlined color="error" fontSize="small" />
            <Typography variant="body2" color="error.main" fontWeight="bold">
              Overdue Maps
            </Typography>
          </Box>
          <List dense disablePadding sx={{ width: "100%" }}>
            {overDue.map((map: any) => (
              <MonitoringList key={map?.map_id} map={map} overdue />
            ))}
          </List>
        </Stack>
      )}

      {/* Maps Due in a Month */}
      {dueInAMonth && dueInAMonth.length > 0 ? (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScheduleOutlined color="warning" fontSize="small" />
              <Typography variant="body2" color="warning.main" fontWeight="bold">
                Maps Due in a Month
              </Typography>
            </Box>
            <Chip 
              label={`Deadline: ${moment().add(30, "days").format("DD/MM/YY")}`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: '16px', '& .MuiChip-label': { fontSize: '0.6rem', px: 0.5 } }}
            />
          </Box>
          <List dense disablePadding sx={{ width: "100%" }}>
            {dueInAMonth.map((map: any) => (
              <MonitoringList key={map?.map_id} map={map} />
            ))}
          </List>
        </Stack>
      ) : (
        <Box sx={{ mt: 1 }}>
          <NoData text="No Maps Due in a month" sx={{ width: "100%", py: 1 }} />
        </Box>
      )}

      {/* Upcoming High Risk Maps */}
      {upcomingHighRiskMaps && upcomingHighRiskMaps.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DateRange color="primary" fontSize="small" />
            <Typography variant="body2" color="primary.main" fontWeight="bold">
              Upcoming High Risk Maps
            </Typography>
          </Box>
          <List dense disablePadding sx={{ width: "100%" }}>
            {upcomingHighRiskMaps.map((map: any) => (
              <MonitoringList key={map?.map_id} map={map} />
            ))}
          </List>
        </Stack>
      )}
    </Box>
  );
};

export default MonitoringDatesSummary;
